import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaHome, FaTimes, FaClipboardList, FaDice, FaBolt,
  FaUser, FaUserFriends, FaLock, FaShieldAlt, FaHistory, FaSignOutAlt,
  FaChevronRight, FaPhone, FaGamepad, FaTrophy, FaTelegramPlane,
} from 'react-icons/fa';
import { Icons } from '@/utils/Icons';
import { useLazyGetMatchesQuery, useLazyGetSportCompetitionsQuery } from '@/store/api/sportsApi';
import { useAppSelector } from '@/hooks';
import { useLogoutMutation } from '@/store/api/authApi';
import { formatNumber } from '@/utils';
import SearchDropdown from '@/components/common/SearchDropdown';
import type { BrandFeature } from '@/brands';
import { isMenuLinkPainted, useBrandFeatures, useBrandMenus } from '@/brands';

/* ───────────────── Types ───────────────── */

type TreeNode = {
  id: string;
  label: string;
  children?: TreeNode[];
  sportName?: string;
  seriesId?: string;
  path?: string;
  /**
   * Brand Feature this row belongs to. Menu Visibility (`id`) is the brand
   * owner hiding a link; this is the super admin revoking the product behind
   * it, which also 404s the path — so the row has to go, not just grey out.
   */
  grant?: BrandFeature;
  /**
   * The backend's `SportType` member to query competitions for, when it differs
   * from the label. `/competitions?sport=` feeds this straight into Prisma, so
   * it has to be an exact enum member — "Horse Racing" is a 500, `HorseRacing`
   * is the row. Absent means the label doubles as the key (Cricket, Tennis);
   * `null` means this backend carries no such sport, so it is never queried.
   */
  sport?: string | null;
};

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  /**
   * Drops the desktop sports rail so the page runs full-width. The mobile
   * drawer always stays — the header's hamburger is the only navigation on
   * small screens, and hiding it would leave a dead button.
   */
  hideDesktopRail?: boolean;
}

/* ───────────────── Mobile MenuItem ───────────────── */
interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  isActive?: boolean;
  badge?: string;
}

function MenuItem({ icon, label, onClick, isActive = false, badge }: MenuItemProps) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-3 w-full px-3 py-2.5 rounded-[12px] text-xs font-semibold transition-all active:scale-95"
      style={{
        background: isActive ? '#5a8a6f' : '#fff',
        color: isActive ? '#fff' : '#374151',
        border: isActive ? 'none' : '1px solid #e5e7eb',
      }}
    >
      <div
        className="w-8 h-8 flex items-center justify-center rounded-[8px] flex-shrink-0"
        style={{ background: isActive ? 'rgba(255,255,255,0.2)' : '#f3f4f6', color: isActive ? '#fff' : '#6b7280' }}
      >
        {icon}
      </div>
      <span className="flex-1 text-left">{label}</span>
      {badge && (
        <div className="text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md" style={{ background: '#ef4444' }}>{badge}</div>
      )}
      {!badge && !isActive && <FaChevronRight size={11} style={{ color: '#d1d5db', flexShrink: 0 }} />}
    </button>
  );
}

/* ───────────────── Desktop Sports Tree ───────────────── */

const racingTree: TreeNode[] = [
  { id: 'horse', label: 'Horse Racing', sport: 'HorseRacing' },
  // `GreyhoundRacing` is commented out of the server's SportType enum, so there
  // is no value to query with — see the note on `sport` above.
  { id: 'greyhound', label: 'Greyhound Racing', sport: null },
];

const othersTree: TreeNode[] = [
  { id: 'casino', label: 'Our Casino', path: '/casino/all', grant: 'casino' },
  { id: 'vip', label: 'Our VIP Casino', path: '/casino/all/live', grant: 'casino' },
  { id: 'premium', label: 'Our Premium Casino', path: '/casino/all/table', grant: 'casino' },
  { id: 'virtual', label: 'Our Virtual', path: '/casino/all/virtual', grant: 'casino' },
  { id: 'live', label: 'Live Casino', path: '/casino/all/live', grant: 'casino' },
  { id: 'slot', label: 'Slot Game', path: '/casino/all/slots', grant: 'casino' },
  // Labelled Fantasy Game, so it answers to the Fantasy switch even though the
  // lobby it opens is served by the casino aggregator.
  { id: 'fantasy', label: 'Fantasy Game', path: '/casino/all/fishing', grant: 'fantasy' },
];

const sportsTree: TreeNode[] = [
  { id: 'politics', label: 'Politics' }, { id: 'cricket', label: 'Cricket' },
  { id: 'football', label: 'Football' }, { id: 'tennis', label: 'Tennis' },
  { id: 'table-tennis', label: 'Table Tennis' }, { id: 'badminton', label: 'Badminton' },
  { id: 'esoccer', label: 'Esoccer' }, { id: 'basketball', label: 'Basketball' },
  { id: 'volleyball', label: 'Volleyball' }, { id: 'snooker', label: 'Snooker' },
  { id: 'ice-hockey', label: 'Ice Hockey' }, { id: 'e-games', label: 'E Games' },
  { id: 'futsal', label: 'Futsal' }, { id: 'handball', label: 'Handball' },
  { id: 'kabaddi', label: 'Kabaddi' }, { id: 'golf', label: 'Golf' },
  { id: 'rugby-league', label: 'Rugby League' }, { id: 'boxing', label: 'Boxing' },
  { id: 'mma', label: 'Mixed Martial Arts' }, { id: 'motogp', label: 'MotoGP' },
  { id: 'american-football', label: 'American Football' }, { id: 'soccer', label: 'Soccer' },
  { id: 'wrestling', label: 'Wrestling' },
];

function DesktopSportsTree() {
  const navigate = useNavigate();
  // Per-brand Menu Visibility. A node's `id` doubles as its super-admin key, so
  // the rail needs no lookup table — an id the panel does not know stays visible.
  const menus = useBrandMenus();
  const { sidebar } = menus;
  // Per-brand Features on top of that: Menu Visibility hides a link the product
  // still has, Features take the product away entirely. Racing and All Sports
  // are the sportsbook, so they stand or fall with the `sports` grant.
  const features = useBrandFeatures();
  const granted = (node: TreeNode) => !node.grant || features[node.grant] !== false;
  const sportsOn = features.sports !== false;
  const shown = (id: string) => sidebar[id as keyof typeof sidebar] !== false;
  // Racing and All Sports rows expand the tree in place, so they own no URL and
  // their own toggle is the whole test. The Others rows DO navigate, and several
  // of them share a page with each other and with the top nav — "Our VIP Casino"
  // and "Live Casino" are both `/casino/all/live`. Hiding any one of those closes
  // the page, so `isMenuLinkPainted` drops the rest rather than leaving the rail
  // with a row that 404s.
  const racing = sportsOn ? racingTree.filter((n) => shown(n.id)) : [];
  const others = othersTree.filter(
    (n) => granted(n) && isMenuLinkPainted(n.path, shown(n.id), menus),
  );
  const allSports = sportsOn ? sportsTree.filter((n) => shown(n.id)) : [];
  const [getCompetitions] = useLazyGetSportCompetitionsQuery();
  const [getMatches] = useLazyGetMatchesQuery();
  const [openSections, setOpenSections] = useState({ racing: true, others: true, allSports: true });
  const [activeSportId, setActiveSportId] = useState<string | null>(null);
  const [activeCompetitionId, setActiveCompetitionId] = useState<string | null>(null);
  const [sportCompetitions, setSportCompetitions] = useState<Record<string, TreeNode[]>>({});
  const [competitionMatches, setCompetitionMatches] = useState<Record<string, TreeNode[]>>({});

  const toggleSection = (key: keyof typeof openSections) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleNode = async (node: TreeNode, level: number) => {
    if (level === 0) {
      const isAlreadyOpen = activeSportId === node.id;
      if (isAlreadyOpen) { setActiveSportId(null); setActiveCompetitionId(null); return; }
      setActiveSportId(node.id);
      setActiveCompetitionId(null);
      if (!sportCompetitions[node.id]) {
        // `sport: null` means this backend has no such sport at all. Querying
        // anyway sends a value Prisma cannot parse and answers 500, so the row
        // opens on an empty list instead.
        const sportKey = node.sport === undefined ? node.label : node.sport;
        const res = sportKey ? await getCompetitions(sportKey).unwrap().catch(() => null) : null;
        setSportCompetitions((prev) => ({
          ...prev,
          [node.id]: res?.competitions?.map((comp) => ({
            id: `comp-${comp.id}`, label: comp.name, sportName: sportKey ?? node.label, seriesId: String(comp.id),
          })) || [],
        }));
      }
      return;
    }
    if (level === 1 && node.seriesId) {
      const isAlreadyOpen = activeCompetitionId === node.seriesId;
      if (isAlreadyOpen) { setActiveCompetitionId(null); return; }
      setActiveCompetitionId(node.seriesId);
      if (!competitionMatches[node.seriesId]) {
        const res = await getMatches({ sportName: node.sportName?.toLowerCase(), seriesId: node.seriesId }).unwrap();
        setCompetitionMatches((prev) => ({
          ...prev,
          [node.seriesId!]: res?.fixture?.map((m) => ({ id: String(m.id), label: m.name, sportName: m.sport })) || [],
        }));
      }
    }
  };

  const TreeItem = ({ node, level = 0, section }: { node: TreeNode; level?: number; section: 'racing' | 'others' | 'allSports' }) => {
    const isOpen = level === 0 ? activeSportId === node.id : level === 1 ? activeCompetitionId === node.seriesId : false;
    const dynamicChildren = level === 0 ? sportCompetitions[node.id] : level === 1 && node.seriesId ? competitionMatches[node.seriesId] : undefined;
    const children = dynamicChildren || node.children;
    // Racing rows expand a competition tree in place exactly like All Sports
    // ones. This used to read `section === 'allSports'`, and since it gates the
    // click as well as the icon, every Racing row fell through `onClick` without
    // matching a single branch — a button that did nothing at all.
    const showToggleIcon =
      (section === 'allSports' || section === 'racing') && (level === 0 || level === 1);
    const paddingLeft = level === 0 ? 15 : level === 1 ? 25 : 35;
    return (
      <div>
        <button
          onClick={() => {
            if (section === 'others') { navigate(node?.path ?? '#'); return; }
            if (level === 2 && node.sportName) { navigate(`/game-details/${node.sportName}/${node.id}`); return; }
            if (showToggleIcon) toggleNode(node, level);
          }}
          className="text-start w-full min-h-[28px] bg-[#d3d3d3] text-[14px] flex items-center gap-2 border-b border-gray-400 hover:bg-gray-300"
          style={{ paddingLeft: `${paddingLeft}px` }}
        >
          {showToggleIcon && (isOpen ? <Icons.MinusSquare size={12} /> : <Icons.PlusSquare size={12} />)}
          {node.label}
        </button>
        {isOpen && children?.map((child) => <TreeItem key={child.id} node={child} level={level + 1} section={section} />)}
        {/* Without this an open row with no competitions renders nothing, which
            is indistinguishable from the click having been ignored. */}
        {isOpen && children && children.length === 0 && (
          <div
            className="min-h-[28px] bg-[#d3d3d3] text-[13px] text-gray-600 italic flex items-center border-b border-gray-400"
            style={{ paddingLeft: `${level === 0 ? 25 : 35}px` }}
          >
            No events
          </div>
        )}
      </div>
    );
  };

  const SectionHeader = ({ title, sectionKey }: { title: string; sectionKey: keyof typeof openSections }) => (
    <div
      onClick={() => toggleSection(sectionKey)}
      className="h-[32px] bg-primary-gradient text-white text-[15px] font-semibold flex items-center justify-between px-3 cursor-pointer"
    >
      {title}
      {openSections[sectionKey] ? <Icons.Up size={12} /> : <Icons.Down size={12} />}
    </div>
  );

  return (
    <div className="text-sm">
      {/* Player's own referral link. The desktop rail is sports-only, so without
          this the affiliate page is unreachable on desktop — it lived solely in
          the mobile drawer. */}
      {shown('referEarn') && (
        <button
          onClick={() => navigate('/user/affiliate')}
          className="h-[32px] w-full bg-primary-gradient text-white text-[15px] font-semibold flex items-center gap-2 px-3 cursor-pointer"
        >
          Refer &amp; Earn
        </button>
      )}

      {/* A section is drawn only if the super admin left it on AND something is
          left inside it — an empty header would be a dead row a player can only
          collapse. */}
      {shown('secRacing') && racing.length > 0 && (
        <>
          <SectionHeader title="Racing Sports" sectionKey="racing" />
          {openSections.racing && racing.map((node) => <TreeItem key={node.id} node={node} section="racing" />)}
        </>
      )}
      {shown('secOthers') && others.length > 0 && (
        <>
          <SectionHeader title="Others" sectionKey="others" />
          {openSections.others && others.map((node) => <TreeItem key={node.id} node={node} section="others" />)}
        </>
      )}
      {shown('secAllSports') && allSports.length > 0 && (
        <>
          <SectionHeader title="All Sports" sectionKey="allSports" />
          {openSections.allSports && allSports.map((node) => <TreeItem key={node.id} node={node} section="allSports" />)}
        </>
      )}
    </div>
  );
}

/* ───────────────── Main Sidebar ───────────────── */

const Sidebar = ({ isOpen, onToggle, hideDesktopRail = false }: SidebarProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { info, walletInfo } = useAppSelector((state) => state.user);
  const [logout] = useLogoutMutation();
  // The drawer is the only navigation on mobile, so a product the super admin
  // revoked has to disappear here too — its rows point at URLs `FeatureGate`
  // now answers 404 for.
  const { sports: sportsOn, casino: casinoOn } = useBrandFeatures();

  const mainWallet = walletInfo?.find((w) => w?.type === 'Main');
  const mainBalance =
    Number(mainWallet?.amount ?? 0) -
    Math.abs(Number(mainWallet?.exposureAmount ?? 0)) -
    Number(mainWallet?.lockedAmount ?? 0);

  const username = info?.username ?? 'Guest';
  const initial = username?.[0]?.toUpperCase() ?? 'G';

  const go = (to: string) => { navigate(to); onToggle(); };

  const handleLogout = async () => {
    try { await logout().unwrap(); } catch {}
    navigate('/login');
    onToggle();
  };

  return (
    <>
      {/* ── Mobile Backdrop ── */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/40 z-[100] xl:hidden" onClick={onToggle} />
      )}

      {/* ── Mobile Drawer ── */}
      <div
        className="fixed inset-y-0 left-0 z-[101] flex flex-col bg-white shadow-2xl transition-transform duration-300 xl:hidden"
        style={{ width: 280, transform: isOpen ? 'translateX(0)' : 'translateX(-100%)' }}
      >
        {/* Drawer Header */}
        <div style={{ background: '#5a8a6f', padding: '16px 16px 20px' }}>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center justify-center rounded-[10px]" style={{ width: 36, height: 36, background: 'rgba(255,255,255,0.15)' }}>
              <span className="text-lg">🏏</span>
            </div>
            <button
              onClick={onToggle}
              className="flex items-center justify-center rounded-[10px] text-white active:scale-90"
              style={{ width: 32, height: 32, background: 'rgba(255,255,255,0.15)' }}
            >
              <FaTimes size={14} />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center rounded-[12px] font-black text-lg flex-shrink-0" style={{ width: 48, height: 48, background: '#fff', color: '#5a8a6f' }}>
              {initial}
            </div>
            <div className="overflow-hidden flex-1 min-w-0">
              <h3 className="font-bold text-sm text-white truncate">{username}</h3>
              <p className="text-white/70 text-[10px] truncate">{info?.role?.name ?? 'User'}</p>
              <div className="mt-1 inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold text-white gap-1" style={{ background: 'rgba(255,255,255,0.2)' }}>
                <span>💰</span>
                <span>{formatNumber(mainBalance)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Drawer Menu */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ background: '#f5f5f5' }}>
          <section>
            <p className="text-[10px] font-bold uppercase tracking-wider px-1 mb-2" style={{ color: '#9ca3af' }}>Platform</p>
            <div className="space-y-1">
              <MenuItem icon={<FaHome size={15} />}          label="Home"              isActive={pathname === '/home'}                    onClick={() => go('/home')} />
              {sportsOn && (
              <MenuItem icon={<FaBolt size={15} />}          label="In-Play"           isActive={pathname === '/inplay'}                  onClick={() => go('/inplay')} />
              )}
              {casinoOn && (
              <>
              <MenuItem icon={<FaDice size={15} />}          label="Our Casino"        isActive={pathname === '/casino/all'}              onClick={() => go('/casino/all')} />
              <MenuItem icon={<FaTrophy size={15} />}        label="Live Casino"       isActive={pathname === '/casino/all/live'}         onClick={() => go('/casino/all/live')} />
              <MenuItem icon={<FaGamepad size={15} />}       label="Slots"             isActive={pathname === '/casino/all/slots'}        onClick={() => go('/casino/all/slots')} />
              </>
              )}
              <MenuItem icon={<FaClipboardList size={15} />} label="My Bets"           isActive={pathname === '/user/bet-history'}        onClick={() => go('/user/bet-history')} />
              <MenuItem icon={<FaHistory size={15} />}       label="Account Statement" isActive={pathname === '/user/account-statement'}  onClick={() => go('/user/account-statement')} />
            </div>
          </section>
          <section>
            <p className="text-[10px] font-bold uppercase tracking-wider px-1 mb-2" style={{ color: '#9ca3af' }}>Account</p>
            <div className="space-y-1">
              <MenuItem icon={<FaUserFriends size={15} />} label="Refer & Earn"     isActive={pathname === '/user/affiliate'}                onClick={() => go('/user/affiliate')} />
              <MenuItem icon={<FaUser size={15} />}      label="Activity Logs"      isActive={pathname === '/user/activity-logs'}             onClick={() => go('/user/activity-logs')} />
              <MenuItem icon={<FaLock size={15} />}      label="Change Password"    isActive={pathname === '/user/change-password'}           onClick={() => go('/user/change-password')} />
              <MenuItem icon={<FaShieldAlt size={15} />} label="Secure Auth"        isActive={pathname === '/user/secure-auth-verification'}  onClick={() => go('/user/secure-auth-verification')} />
              <MenuItem icon={<FaPhone size={15} />}     label="Terms & Conditions" isActive={pathname === '/terms-and-conditions'}           onClick={() => go('/terms-and-conditions')} />
              <MenuItem icon={<FaTelegramPlane size={15} />} label="Contact Us"      isActive={pathname === '/contact'}                            onClick={() => go('/contact')} />
            </div>
          </section>
        </div>

        {/* Drawer Footer */}
        <div className="p-4 border-t bg-white" style={{ borderColor: '#f3f4f6' }}>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 p-3 rounded-[12px] font-semibold text-sm active:scale-95 transition-all"
            style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-[8px]" style={{ background: '#ef4444' }}>
              <FaSignOutAlt size={14} style={{ color: '#fff' }} />
            </div>
            <span>Log Out</span>
          </button>
          <p className="text-center text-[9px] font-semibold uppercase mt-3" style={{ color: '#d1d5db' }}>
            Cricket Exchange · v1.0.0
          </p>
        </div>
      </div>

      {/* ── Desktop Sidebar (sports tree) ── */}
      {!hideDesktopRail && (
        <aside className="hidden xl:block w-[240px] 2xl:w-[280px] bg-white border-r overflow-y-auto shrink-0">
          <DesktopSportsTree />
        </aside>
      )}
    </>
  );
};

export default Sidebar;
